import React from 'react';
import { Link } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/lib/i18n';

const Footer = React.memo(() => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  return (
    <footer className="bg-black text-white py-12 border-t border-[#FFCF43]/15">
      <div className="singu-container">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <h2 className="text-2xl font-extrabold mb-4 text-[#FFCF43] flex items-center gap-2">
              <span className="text-4xl px-2 bg-black/80 rounded">FT!</span>
              <span className="sr-only">{t('heroTitle')}</span>
            </h2>
            <p className="text-gray-300 max-w-md">
              {t('heroTagline')}<br />
              <span className="text-[#FFCF43]">{t('heroTitle')},</span> {t('heroSubtitle')}
            </p>
          </div>
          
          <div>
            <h3 className="font-bold mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li><a href="#about" className="text-gray-300 hover:text-white transition-colors">{t('about')}</a></li>
              <li><a href="#benefits" className="text-gray-300 hover:text-white transition-colors">{t('benefits')}</a></li>
              <li><a href="#who" className="text-gray-300 hover:text-white transition-colors">{t('targetAudience')}</a></li>
              <li><a href="#join" className="text-gray-300 hover:text-white transition-colors">{t('join')}</a></li>
              <li><Link to="/members" className="text-gray-300 hover:text-white transition-colors">{t('members')}</Link></li>
              <li><Link to="/faq" className="text-gray-300 hover:text-white transition-colors">Centre d'aide</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold mb-4">Contactez-nous</h3>
            <p className="text-gray-300">{t('contact')}</p>
            <div className="mt-4 flex space-x-4">
              <a href="#" className="text-gray-300 hover:text-white">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-gray-300 hover:text-white">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        <div className="mt-12 border-t border-[#FFCF43]/10 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-gray-300 text-sm">
                &copy; {new Date().getFullYear()} {t('heroTitle')}. {t('rights')}
              </p>
              {/* 🔥 PHASE 4: Mention BETA pour conformité Loi 1807 */}
              <p className="text-xs text-gray-400 mt-1">
                Version BETA • Données stockées conformément au RGPD et Loi 1807
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex space-x-6">
                <Link to="/terms" className="text-gray-300 hover:text-[#FFCF43] text-sm">CGU</Link>
                <Link to="/privacy-policy" className="text-gray-300 hover:text-[#FFCF43] text-sm">{t('privacy')}</Link>
                <Link to="/legal/international-compliance" className="text-gray-300 hover:text-[#FFCF43] text-sm">Conformité</Link>
              </div>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
